import {FC, useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {Card, Row, Col, Button, Image, Tabs, Tab} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../../_metronic/layout/core'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useAuth} from '../../../modules/auth'
import {getCGEMetadata, runCGESimulation, runCGECommoditySimulation} from './core/_requests'
import {
  CGEMetadata,
  CGESimulateRequest,
  CGESimulateResponse,
  CGECommodityPairRequest,
  CGEShock,
} from './core/_models'

import {CGEShockForm} from './components/CGEShockForm'
import {CGECommodityShockForm} from './components/CGECommodityShockForm'
import {CGEHeadlineCards} from './components/CGEHeadlineCards'
import {CGERegionsTable} from './components/CGERegionsTable'
import {CGERegionsChart} from './components/CGERegionsChart'
import {CGESectorImpactChart} from './components/CGESectorImpactChart'
import {CGETradeChart} from './components/CGETradeChart'
import {CGEShocksAppliedTable} from './components/CGEShocksAppliedTable'
import {CGESimulationLoading} from './components/CGESimulationLoading'

const ReactSwal = withReactContent(Swal)

// Only the Bangladesh-EU bundle exists so far. Gate on economy for now.
// Swap for a real CASL 'MENU.CGE' permission once a backend dss_menus row exists.
const ALLOWED_ECONOMIES = ['BGD']

const CGEPage: FC = () => {
  const intl = useIntl()
  const axiosPrivate = useAxiosPrivate()
  const {currentUser} = useAuth()
  const [metadata, setMetadata] = useState<CGEMetadata | null>(null)
  const [showSettingSection, setShowSettingSection] = useState(true)
  const [showResultSection, setShowResultSection] = useState(false)
  const [result, setResult] = useState<CGESimulateResponse | null>(null)
  const [runningMethod, setRunningMethod] = useState<'johansen' | 'gragg' | null>(null)
  const [shockMode, setShockMode] = useState<'direct' | 'commodity'>('direct')

  useEffect(() => {
    getCGEMetadata(axiosPrivate)
      .then((res) => setMetadata(res.data))
      .catch((e) => console.error(e))
  }, [])

  if (!ALLOWED_ECONOMIES.includes(currentUser?.economy!)) {
    return <NoPermissionCard />
  }

  const handleSimulationPromise = (
    promise: Promise<{data: CGESimulateResponse}>,
    method: 'johansen' | 'gragg'
  ) => {
    setShowResultSection(true)
    setShowSettingSection(false)
    setResult(null)
    setRunningMethod(method)
    promise
      .then((res) => {
        if (res.data.status === 'ok') {
          setResult(res.data)
        } else {
          setShowResultSection(false)
          setShowSettingSection(true)
          ReactSwal.fire({
            title: 'Simulation failed',
            html: 'The scenario could not be solved. Please check your parameters and try again.',
            icon: 'error',
            showCloseButton: true,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: '<i class="fa-solid fa-angles-left me-1 pb-1"></i>BACK',
            customClass: {title: 'fw-bold', cancelButton: 'btn btn-danger btn-sm'},
            padding: '1rem',
          })
        }
      })
      .catch((e) => {
        console.error(e)
        setShowResultSection(false)
        setShowSettingSection(true)
        ReactSwal.fire({
          title: 'Simulation error',
          html:
            e?.response?.data?.message ||
            'Something went wrong while running the simulation. Please try again.',
          icon: 'error',
          showCloseButton: true,
          showConfirmButton: false,
          showCancelButton: true,
          cancelButtonText: '<i class="fa-solid fa-angles-left me-1 pb-1"></i>BACK',
          customClass: {title: 'fw-bold', cancelButton: 'btn btn-danger btn-sm'},
          padding: '1rem',
        })
      })
  }

  const handleRunSimulation = (req: CGESimulateRequest) =>
    handleSimulationPromise(runCGESimulation(axiosPrivate, req), req.method)

  const handleRunCommoditySimulation = (req: {
    closure: string
    method: 'johansen' | 'gragg'
    pairs: CGECommodityPairRequest[]
    direct_shocks: CGEShock[]
  }) =>
    handleSimulationPromise(
      runCGECommoditySimulation(axiosPrivate, {
        bundle_id: metadata?.bundle_id,
        closure: req.closure,
        method: req.method,
        focus: metadata?.focus || 'bgd',
        pairs: req.pairs,
        direct_shocks: req.direct_shocks,
      }),
      req.method
    )

  const handleAdjustParams = () => {
    setShowSettingSection(true)
    setShowResultSection(false)
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {intl.formatMessage({id: 'MENU.CGE'})}
      </PageTitle>

      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex justify-content-start align-items-center'>
              <Image src='media/tina/img-result-documents.jpg' thumbnail width={110} />
              <div className='ms-5 d-flex flex-column justify-content-start'>
                <h3 className='text-primary'>
                  CGE Tariff Simulator
                  <span className='text-gray-700'>
                    <span className='fa-solid fa-angles-right px-1'></span>
                    {/* {metadata?.label} */}
                    Bangladesh (10 region x 10 sector)
                  </span>
                </h3>
                <div className='text-gray-700'>
                  Run a GTAP12 computable general equilibrium (CGE) simulation: pick a sector, an
                  exporting region, an importing region and a tariff change, and see the welfare,
                  terms-of-trade, GDP and trade effects across every modeled region.
                </div>
                <div className='text-gray-700 mt-2'>
                  <strong>How to use:</strong> add a row for each sector shock (sector, exporting
                  region, importing region, tariff % change), choose a closure, then click{' '}
                  <em>Run simulation</em>.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showSettingSection && (
        <Row className='mb-5'>
          <Col md={12}>
            <Tabs
              activeKey={shockMode}
              onSelect={(k) => setShockMode((k as 'direct' | 'commodity') ?? 'direct')}
              className='mb-3'
            >
              <Tab eventKey='direct' title='Direct sector shock'>
                <CGEShockForm metadata={metadata} onSubmit={handleRunSimulation} />
              </Tab>
              <Tab eventKey='commodity' title='Commodity-level (HS) tariffs'>
                <CGECommodityShockForm
                  metadata={metadata}
                  onSubmit={handleRunCommoditySimulation}
                />
              </Tab>
            </Tabs>
          </Col>
        </Row>
      )}

      {showResultSection && (
        <>
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100 d-flex justify-content-between align-items-center'>
                    <h3>
                      <i className='fa-regular fa-star me-5'></i>
                      Simulation headline results
                    </h3>
                    <Button
                      className='btn-warning text-black fw-bold'
                      size='sm'
                      onClick={handleAdjustParams}
                    >
                      <i className='text-black bi bi-sliders me-3'></i>
                      Adjust Parameters
                    </Button>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {result ? (
                    <CGEHeadlineCards headline={result.headline} regions={result.regions} />
                  ) : (
                    <CGESimulationLoading method={runningMethod ?? 'johansen'} />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {result && (
            <>
              <Row className='mb-5'>
                <Col md={12}>
                  <Card>
                    <Card.Header className='card-header-stretch'>
                      <Card.Title>
                        <h3>Welfare by region</h3>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <CGERegionsChart regions={result.regions} />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className='mb-5'>
                <Col md={12}>
                  <Card>
                    <Card.Header className='card-header-stretch'>
                      <Card.Title>
                        <h3>Region detail</h3>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <CGERegionsTable
                        rows={result.regions}
                        focusCode={result.headline.focus_code}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className='mb-5'>
                <Col>
                  <Card>
                    <Card.Header className='card-header-stretch'>
                      <Card.Title>
                        <h3>
                          {result.focus_detail.sector_output_pct.length > 0
                            ? `${result.focus_detail.name} sector output impact`
                            : 'Applied tariff shocks'}
                        </h3>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      {result.focus_detail.sector_output_pct.length > 0 ? (
                        <CGESectorImpactChart
                          focusName={result.focus_detail.name}
                          sectors={result.focus_detail.sector_output_pct}
                        />
                      ) : (
                        <CGEShocksAppliedTable shocks={result.shocks_applied} metadata={metadata} />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className='mb-5'>
                {result.trade_impacts.map((ti) => (
                  <Col md={12} className='mb-5' key={ti.good}>
                    <Card>
                      <Card.Header className='card-header-stretch'>
                        <Card.Title>
                          <h3>Export volume change by region | {ti.name}</h3>
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <CGETradeChart
                          goodName={ti.name}
                          volumes={ti.export_volume_by_region_pct}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row className='mb-5'>
                <Col>
                  <small className='text-muted'>
                    Engine: {result.engine.bundle_id} / {result.engine.closure} /{' '}
                    {result.engine.method} | solved in {result.solve.seconds}s
                    {result.solve.residual !== null && (
                      <>, residual {result.solve.residual.toExponential(2)}</>
                    )}
                    {result.solve.walras_slack !== null && (
                      <>, Walras slack {result.solve.walras_slack.toExponential(2)}</>
                    )}
                  </small>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </>
  )
}

export default CGEPage
